/**
 * ISO 22301 to BCI GPG Professional Practice Mapping
 * Source of truth for audit compliance tracking
 */

export const ISO_22301_MAPPING = [
  {
    clause: "4.1-4.2",
    title: "Context & Interested Parties",
    mappedPP: "PP1",
    ppName: "Policy & Programme Management",
    evidence: [
      "Verify documentation of internal/external issues",
      "Clear list of interested parties (stakeholders) and their requirements",
      "Context analysis documented and reviewed"
    ],
    auditFocus: "Auditors will look for documented analysis of the organization's context, including internal and external factors that affect the BCMS, and a comprehensive stakeholder register."
  },
  {
    clause: "4.3",
    title: "Scope of the BCMS",
    mappedPP: "PP1",
    ppName: "Policy & Programme Management",
    evidence: [
      "Documented scope statement identifying boundaries",
      "Applicability and justifications for exclusions",
      "Scope covers all products and services"
    ],
    auditFocus: "Auditors will check for a clearly defined and documented BCMS scope that specifies what is included, what is excluded, and the justifications for any exclusions."
  },
  {
    clause: "5.1-5.2",
    title: "Leadership & Policy",
    mappedPP: "PP1",
    ppName: "Policy & Programme Management",
    evidence: [
      "Top management approval of BC Policy",
      "Evidence of resource allocation",
      "Management commitment to continual improvement"
    ],
    auditFocus: "Auditors will confirm executive sponsorship through signed policies, budget allocations, and evidence of management reviews."
  },
  {
    clause: "5.3",
    title: "Roles & Responsibilities",
    mappedPP: "PP1",
    ppName: "Policy & Programme Management",
    evidence: [
      "Defined roles and responsibilities in job descriptions",
      "Performance plans with BC responsibilities",
      "Alternates assigned for key response roles"
    ],
    auditFocus: "Auditors will examine role definitions, RACI matrices, and verify that backup personnel are identified for critical positions."
  },
  {
    clause: "6.1-6.2",
    title: "Planning & Objectives",
    mappedPP: "PP1",
    ppName: "Policy & Programme Management",
    evidence: [
      "BC objectives documented and measurable",
      "Objectives aligned with strategic direction",
      "Risk-based planning approach"
    ],
    auditFocus: "Auditors will validate that BCM objectives are SMART (Specific, Measurable, Achievable, Relevant, Time-bound) and linked to organizational strategy."
  },
  {
    clause: "7.2-7.3",
    title: "Competence & Awareness",
    mappedPP: "PP2",
    ppName: "Embedding Business Continuity",
    evidence: [
      "Training records and competence assessments",
      "Evidence of general awareness programs",
      "Induction training documentation"
    ],
    auditFocus: "Auditors will review training matrices, attendance records, competency assessments, and verify that all personnel understand their BC responsibilities."
  },
  {
    clause: "7.4-7.5",
    title: "Communication & Documentation",
    mappedPP: "PP1 & PP2",
    ppName: "Policy & Programme Management / Embedding",
    evidence: [
      "Formal communication plan for internal and external parties",
      "Document control procedures (versioning, distribution, storage)",
      "Communication testing records"
    ],
    auditFocus: "Auditors will check communication protocols, document management systems, and verify that information reaches the right people at the right time."
  },
  {
    clause: "8.2.2",
    title: "Business Impact Analysis (BIA)",
    mappedPP: "PP3",
    ppName: "Analysis",
    evidence: [
      "BIA process identifies prioritized activities",
      "MTPD, RTO, RPO documented for products and services",
      "MBCO (Minimum Business Continuity Objective) defined"
    ],
    auditFocus: "Auditors will verify that BIA methodology is systematic, covers all critical functions, and produces actionable recovery objectives."
  },
  {
    clause: "8.2.3",
    title: "Risk Assessment",
    mappedPP: "PP3",
    ppName: "Analysis",
    evidence: [
      "Systematic risk and threat assessment",
      "Identification of unacceptable risks",
      "Single points of failure (SPOF) documented"
    ],
    auditFocus: "Auditors will examine risk registers, threat scenarios, and verify that risk treatments are proportionate to the identified risks."
  },
  {
    clause: "8.3",
    title: "Strategies & Solutions",
    mappedPP: "PP4",
    ppName: "Design",
    evidence: [
      "Selection process for BC solutions documented",
      "Options evaluated (Replication, Standby, Diversification)",
      "Solutions address BIA requirements"
    ],
    auditFocus: "Auditors will check that strategy selection is justified based on BIA outputs and cost-benefit analysis."
  },
  {
    clause: "8.4",
    title: "Plans & Procedures",
    mappedPP: "PP5",
    ppName: "Implementation",
    evidence: [
      "Documented BC plans (Strategic, Tactical, Operational)",
      "Clear activation criteria and escalation procedures",
      "Plans are accessible and easy to find"
    ],
    auditFocus: "Auditors will review plan structure, accessibility, and verify that procedures are clear enough to be executed under stress."
  },
  {
    clause: "8.5",
    title: "Exercising & Testing",
    mappedPP: "PP6",
    ppName: "Validation",
    evidence: [
      "Exercise schedule/program documented",
      "Post-exercise reports (debriefs) available",
      "Lessons learned are tracked and actioned"
    ],
    auditFocus: "Auditors will examine exercise frequency, scope, participation records, and evidence of continuous improvement from lessons learned."
  },
  {
    clause: "9.1",
    title: "Monitoring & Evaluation",
    mappedPP: "PP6",
    ppName: "Validation",
    evidence: [
      "KPIs and regular progress reports",
      "Top management reviews BCMS performance",
      "Trend analysis and metrics tracking"
    ],
    auditFocus: "Auditors will look for dashboards, management review minutes, and evidence that performance data drives decisions."
  },
  {
    clause: "9.2",
    title: "Internal Audit",
    mappedPP: "PP6",
    ppName: "Validation",
    evidence: [
      "Independent audit reports available",
      "Remedial action plans for non-conformities",
      "Audit program with defined frequency"
    ],
    auditFocus: "Auditors will verify audit independence, coverage of all BCMS elements, and that findings are addressed systematically."
  },
  {
    clause: "10.1",
    title: "Non-conformity & Improvement",
    mappedPP: "PP6",
    ppName: "Validation",
    evidence: [
      "Process for identifying shortfalls documented",
      "Corrective actions implemented",
      "Continual improvement evidence"
    ],
    auditFocus: "Auditors will check the CAPA (Corrective and Preventive Action) process and verify that the BCMS evolves based on lessons learned."
  }
];

// PDCA Cycle mapping to Professional Practices
export const PDCA_MAPPING = {
  plan: {
    phase: "Plan",
    description: "Establish BCM policy, objectives, and processes",
    practices: ["PP1", "PP3"],
    clauses: ["4.1-4.2", "4.3", "5.1-5.2", "5.3", "6.1-6.2", "8.2.2", "8.2.3"],
    color: "from-blue-500 to-blue-600"
  },
  do: {
    phase: "Do",
    description: "Implement strategies, plans, and procedures",
    practices: ["PP2", "PP4", "PP5"],
    clauses: ["7.2-7.3", "7.4-7.5", "8.3", "8.4"],
    color: "from-emerald-500 to-emerald-600"
  },
  check: {
    phase: "Check",
    description: "Monitor, measure, and evaluate performance",
    practices: ["PP6"],
    clauses: ["8.5", "9.1", "9.2"],
    color: "from-amber-500 to-amber-600"
  },
  act: {
    phase: "Act",
    description: "Take actions for continual improvement",
    practices: ["PP6"],
    clauses: ["10.1"],
    color: "from-purple-500 to-purple-600"
  }
};

// Evidence checklist items by Professional Practice
export const PP_EVIDENCE_CHECKLISTS = {
  PP1: {
    name: "Policy & Programme Management",
    isoClauses: ["4.1-4.2", "4.3", "5.1-5.2", "5.3", "6.1-6.2", "7.4-7.5"],
    checklist: [
      { id: "pp1-1", text: "BC Policy approved by top management", clause: "5.1-5.2" },
      { id: "pp1-2", text: "BCMS scope documented with boundaries", clause: "4.3" },
      { id: "pp1-3", text: "Stakeholder register maintained", clause: "4.1-4.2" },
      { id: "pp1-4", text: "Roles and responsibilities defined", clause: "5.3" },
      { id: "pp1-5", text: "BCM objectives are measurable", clause: "6.1-6.2" },
      { id: "pp1-6", text: "Communication plan documented", clause: "7.4-7.5" },
      { id: "pp1-7", text: "Document control procedures in place", clause: "7.4-7.5" }
    ]
  },
  PP2: {
    name: "Embedding Business Continuity",
    isoClauses: ["7.2-7.3", "7.4-7.5"],
    checklist: [
      { id: "pp2-1", text: "Training program documented", clause: "7.2-7.3" },
      { id: "pp2-2", text: "Competency assessments completed", clause: "7.2-7.3" },
      { id: "pp2-3", text: "Awareness sessions conducted", clause: "7.2-7.3" },
      { id: "pp2-4", text: "Training records maintained", clause: "7.2-7.3" },
      { id: "pp2-5", text: "Culture assessment performed", clause: "7.2-7.3" }
    ]
  },
  PP3: {
    name: "Analysis (BIA & Risk Assessment)",
    isoClauses: ["8.2.2", "8.2.3"],
    checklist: [
      { id: "pp3-1", text: "BIA methodology documented", clause: "8.2.2" },
      { id: "pp3-2", text: "Critical functions identified", clause: "8.2.2" },
      { id: "pp3-3", text: "MTPD defined for each function", clause: "8.2.2" },
      { id: "pp3-4", text: "RTO and RPO documented", clause: "8.2.2" },
      { id: "pp3-5", text: "Risk assessment completed", clause: "8.2.3" },
      { id: "pp3-6", text: "Threat scenarios identified", clause: "8.2.3" },
      { id: "pp3-7", text: "Single points of failure documented", clause: "8.2.3" }
    ]
  },
  PP4: {
    name: "Design (Strategies & Solutions)",
    isoClauses: ["8.3"],
    checklist: [
      { id: "pp4-1", text: "Strategy options evaluated", clause: "8.3" },
      { id: "pp4-2", text: "Cost-benefit analysis performed", clause: "8.3" },
      { id: "pp4-3", text: "Solutions address BIA requirements", clause: "8.3" },
      { id: "pp4-4", text: "Resource requirements documented", clause: "8.3" }
    ]
  },
  PP5: {
    name: "Implementation (Plans & Procedures)",
    isoClauses: ["8.4"],
    checklist: [
      { id: "pp5-1", text: "Strategic BC plan documented", clause: "8.4" },
      { id: "pp5-2", text: "Tactical recovery plans in place", clause: "8.4" },
      { id: "pp5-3", text: "Operational procedures documented", clause: "8.4" },
      { id: "pp5-4", text: "Activation criteria defined", clause: "8.4" },
      { id: "pp5-5", text: "Escalation procedures clear", clause: "8.4" },
      { id: "pp5-6", text: "Plans are accessible to responders", clause: "8.4" }
    ]
  },
  PP6: {
    name: "Validation (Exercising & Review)",
    isoClauses: ["8.5", "9.1", "9.2", "10.1"],
    checklist: [
      { id: "pp6-1", text: "Exercise program established", clause: "8.5" },
      { id: "pp6-2", text: "Post-exercise reports completed", clause: "8.5" },
      { id: "pp6-3", text: "Lessons learned tracked", clause: "8.5" },
      { id: "pp6-4", text: "Performance KPIs defined", clause: "9.1" },
      { id: "pp6-5", text: "Management reviews conducted", clause: "9.1" },
      { id: "pp6-6", text: "Internal audits performed", clause: "9.2" },
      { id: "pp6-7", text: "Corrective actions implemented", clause: "10.1" }
    ]
  }
};

// Get ISO clauses for a specific PP
export const getClausesForPP = (pp) => {
  return ISO_22301_MAPPING.filter(item => item.mappedPP.includes(pp));
};

// Get PP for a specific clause
export const getPPForClause = (clause) => {
  const mapping = ISO_22301_MAPPING.find(item => item.clause === clause);
  return mapping ? mapping.mappedPP : null;
};

// Get audit focus for a clause
export const getAuditFocus = (clause) => {
  const mapping = ISO_22301_MAPPING.find(item => item.clause === clause);
  return mapping ? mapping.auditFocus : null;
};
